import React, { useEffect, useState } from "react";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import FormSubmitButton from "../../../Utility/Button/FormSubmitButton";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import { RiCloseLine } from "react-icons/ri";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

const TabMappingAddModel = ({
  onClose,
  onSave,
  sourceTagOptions = [],
  accountingTagOptions = [],
  /** When set, modal is in edit mode (PATCH /tag-mappings/:id). */
  existingMapping = null,
}) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ sourceTagId: "", targetTagId: "" });
  const [submitError, setSubmitError] = useState("");

  const isEdit = Boolean(existingMapping?.id);

  useEffect(() => {
    if (existingMapping?.id) {
      setForm({
        sourceTagId: String(existingMapping.sourceTagId ?? ""),
        targetTagId: String(existingMapping.targetTagId ?? ""),
      });
    } else {
      setForm({ sourceTagId: "", targetTagId: "" });
    }
  }, [existingMapping]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (saving) return;
    setSubmitError("");
    if (!form.sourceTagId || !form.targetTagId) {
      setSubmitError("Please select both Client Tag and Accounting Category.");
      return;
    }

    setSaving(true);
    try {
      await onSave({
        sourceTagId: form.sourceTagId,
        targetTagId: form.targetTagId,
        ...(isEdit ? { id: existingMapping.id } : {}),
      });
      onClose();
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Failed to save mapping."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-primary rounded-lg shadow-lg w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <Typography variant="h5" className="font-semibold text-text-primary">
            {isEdit ? "Edit Client Tag Mapping" : "Add Client Tag Mapping"}
          </Typography>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="!text-text-secondary hover:!text-text-primary transition-colors !border-0 !shadow-none !bg-transparent !p-0 !px-0 !py-0 !font-normal !inline-flex !items-center !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RiCloseLine className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {submitError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
              <Typography className="text-red-700 text-sm">{submitError}</Typography>
            </div>
          )}
          <Dropdown
            label="Client Tag"
            name="sourceTagId"
            value={form.sourceTagId}
            options={sourceTagOptions}
            onChange={handleChange}
            searchable
            placeholder="Select client tag"
          />

          <Dropdown
            label="Accounting Category"
            name="targetTagId"
            value={form.targetTagId}
            options={accountingTagOptions}
            onChange={handleChange}
            searchable
            placeholder="Select accounting category"
          />
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <Button variant="" className="btn-reset" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <FormSubmitButton
            type="button"
            onClick={handleSave}
            loading={saving}
            label={isEdit ? "Save changes" : "Save mapping"}
            loadingText="Saving..."
            idleIconClass=""
          />
        </div>
      </div>
    </div>
  );
};

export default TabMappingAddModel;
